import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Observable, of, switchMap, map, tap, finalize, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { FeatureCollection } from '@turf/turf';
import { environment } from '../../../environments/environment';
import { PdfExportContextService } from './pdf-export-context.service';
import {
  CreateDashboardRequest,
  Dashboard,
  DashboardTemplate,
  WidgetDefinition,
  DashboardApiResponse,
  DashboardResponse,
  ReportDetail,
  ReportListItem,
} from '../models/dashboard.model';
import {
  MarketPositionRequest,
  MarketPositionResponse,
  MarketPositionApiResponse,
} from '../models/market-position.model';
import {
  dashError,
  dashGroupCollapsed,
  dashGroupEnd,
  dashLog,
  dashWarn,
  maskToken,
  safeStringify,
} from '../utils/dashboard-debug';
import { TokenService } from '../../pages/auth/services/token.service';

@Injectable({
  providedIn: 'root'
})
export class DashboardApiService {
  private http = inject(HttpClient);
  private tokenService = inject(TokenService);
  private pdfContext = inject(PdfExportContextService);
  private readonly API_URL = environment.apiUrl;

  private get baseUrl(): string {
    if (this.pdfContext.isPdfMode) {
      return 'http://php';
    }
    return this.API_URL;
  }

  /**
   * Получить токен текущего пользователя/госта для авторизации в dashboards API.
   * (AuthInterceptor пропускает /dashboards, поэтому ставим заголовок здесь.)
   */
  private getAuthToken(): Observable<string> {
    const token = this.tokenService.getTokenAsGuestOrNormal();
    if (!token) {
      dashError('No auth token available for dashboard API', {});
      return throwError(() => new Error('No auth token available for dashboard API'));
    }

    dashLog('Using auth token for dashboard API', { token: maskToken(token) });
    return of(token);
  }

  /**
   * Perare API (/perare-api/v1/) expects a project/API key, not the user JWT.
   * AuthInterceptor skips adding auth for perare-api, so we set the key here.
   * Uses the same key source as dashboard-data.service for consistency.
   */
  private getPerareApiKey(): Observable<string> {
    const perareApiKey = 'YWRhMDc1NGI0ZmIxNzU2MDVhZDJkODZlZTE0MzZlZmQ=';
    dashLog('Using perare-api key for /perare-api/v1/', { key: maskToken(perareApiKey) });
    return of(perareApiKey);
  }

  /**
   * Создать заголовки с авторизацией для API
   */
  private createAuthHeaders(apiKey: string, projectId?: string): HttpHeaders {
    let headers = new HttpHeaders({
      'Authorization': `Bearer ${apiKey}`
    });

    // Добавить x-company header если есть projectId
    if (projectId) {
      headers = headers.set('x-company', projectId);
    }

    return headers;
  }

  /**
   * Widgets Management
   */
  createWidget(widget: WidgetDefinition, projectId?: string): Observable<WidgetDefinition> {
    dashGroupCollapsed('API createWidget', {
      projectId,
      type: (widget as any)?.type,
      title: (widget as any)?.title,
      version: (widget as any)?.version,
    });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<any>(`${this.baseUrl}/api/widgets`, widget, { headers });
      }),
      map((response) => {
        // Handle wrapped response { result: "success", data: {...} }
        const widgetData = response?.data || response;
        const widgetId = widgetData?.uuid || widgetData?.id;
        return {
          uuid: widgetId,
          id: widgetData?.id || widgetId,
          title: widgetData?.title || widget.title,
          description: widgetData?.description || widget.description,
          type: widgetData?.type || widget.type,
          version: widgetData?.version || widget.version,
          endpoints: widgetData?.endpoints || widget.endpoints,
        } as WidgetDefinition;
      }),
      tap({
        next: (res) =>
          dashLog('createWidget OK', {
            id: res?.id || res?.uuid,
            uuid: res?.uuid,
            title: res?.title,
            type: res?.type,
          }),
        error: (err) =>
          dashError('createWidget FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  getWidgets(projectId?: string): Observable<WidgetDefinition[]> {
    dashGroupCollapsed('API getWidgets', { projectId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get<WidgetDefinition[]>(`${this.baseUrl}/api/widgets`, { headers });
      }),
      tap({
        next: (res) =>
          dashLog('getWidgets OK', {
            count: Array.isArray(res) ? res.length : 0,
          }),
        error: (err) =>
          dashError('getWidgets FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Dashboard Templates
   */
  createTemplate(template: DashboardTemplate, projectId?: string): Observable<DashboardTemplate> {
    dashGroupCollapsed('API createTemplate', {
      projectId,
      title: (template as any)?.title,
      version: (template as any)?.version,
      widgetConfigsCount: Array.isArray((template as any)?.widgetConfigs) ? (template as any).widgetConfigs.length : 0,
    });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<DashboardTemplate>(`${this.baseUrl}/api/dashboard-templates`, template, { headers });
      }),
      tap({
        next: (res) =>
          dashLog('createTemplate OK', {
            id: (res as any)?.id,
            title: (res as any)?.title,
            version: (res as any)?.version,
          }),
        error: (err) =>
          dashError('createTemplate FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  getTemplates(projectId?: string): Observable<DashboardTemplate[]> {
    dashGroupCollapsed('API getTemplates', { projectId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get<DashboardTemplate[]>(`${this.baseUrl}/api/dashboard-templates`, { headers });
      }),
      tap({
        next: (res) =>
          dashLog('getTemplates OK', {
            count: Array.isArray(res) ? res.length : 0,
          }),
        error: (err) =>
          dashError('getTemplates FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Project Dashboards
   */
  createDashboard(projectId: string, request: CreateDashboardRequest): Observable<Dashboard> {
    dashGroupCollapsed('API createDashboard', {
      projectId,
      templateId: (request as any)?.templateId,
      title: (request as any)?.title,
      uuid: (request as any)?.uuid,
    });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<any>(`${this.baseUrl}/api/${projectId}/dashboards`, request, { headers });
      }),
      map((raw) => {
        // Some endpoints return { id, widgetConfigs } (DashboardResponse shape),
        // others may return { uuid, widgets } (Dashboard shape). Normalize here.
        const res: any = raw?.data ?? raw;
        const dashboardId: string | undefined = res?.id ?? res?.uuid;
        const plotUuid: string | undefined =
          res?.plotUuid ?? (res?.id && res?.uuid ? res.uuid : undefined);

        return {
          uuid: dashboardId || '',
          projectId: res?.projectId ?? projectId,
          templateId: res?.templateId ?? request.templateId,
          title: res?.title ?? request.title,
          widgets: res?.widgets ?? res?.widgetConfigs ?? [],
          createdAt: res?.createdAt ?? new Date().toISOString(),
          updatedAt: res?.updatedAt ?? res?.createdAt ?? new Date().toISOString(),
          plotUuid,
        } as Dashboard;
      }),
      tap({
        next: (res) =>
          dashLog('createDashboard OK', {
            uuid: (res as any)?.uuid ?? (res as any)?.id,
            title: (res as any)?.title,
          }),
        error: (err) =>
          dashError('createDashboard FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  getProjectDashboards(projectId: string): Observable<Dashboard[]> {
    dashGroupCollapsed('API getProjectDashboards', { projectId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get<DashboardApiResponse>(`${this.baseUrl}/api/${projectId}/dashboards`, { headers });
      }),
      map(response => {
        dashLog('getProjectDashboards raw response', {
          hasResult: !!(response as any)?.result,
          hasData: Array.isArray((response as any)?.data),
          dataCount: Array.isArray((response as any)?.data) ? (response as any).data.length : 0,
        });
        // Extract data array from response
        const dashboardsData = response?.data || [];
        // Transform DashboardResponse to Dashboard format
        return dashboardsData.map((d: DashboardResponse) => ({
          uuid: d.id,
          projectId: d.projectId,
          templateId: d.templateId,
          title: d.title,
          widgets: d.widgetConfigs || [],
          createdAt: d.createdAt,
          updatedAt: d.createdAt, // Use createdAt as fallback if updatedAt is not provided
          plotUuid: (d as any)?.uuid ?? (d as any)?.plotUuid,
        }));
      }),
      tap({
        next: (res) =>
          dashLog('getProjectDashboards OK', {
            count: Array.isArray(res) ? res.length : 0,
            ids: Array.isArray(res) ? res.slice(0, 5).map((d) => d.uuid) : [],
          }),
        error: (err) =>
          dashError('getProjectDashboards FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  getDashboard(projectId: string, dashboardId: string): Observable<Dashboard> {
    dashGroupCollapsed('API getDashboard', { projectId, dashboardId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get<Dashboard>(`${this.baseUrl}/api/${projectId}/dashboards/${dashboardId}`, { headers });
      }),
      tap({
        next: (res) =>
          dashLog('getDashboard OK', {
            uuid: (res as any)?.uuid ?? (res as any)?.id,
            title: (res as any)?.title,
            widgetsCount: Array.isArray((res as any)?.widgets) ? (res as any).widgets.length : 0,
          }),
        error: (err) =>
          dashError('getDashboard FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  renameDashboard(projectId: string, dashboardId: string, newName: string): Observable<Dashboard> {
    dashGroupCollapsed('API renameDashboard', { projectId, dashboardId, newName });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.put<any>(`${this.API_URL}/api/${projectId}/dashboards/${dashboardId}/rename`, { title: newName }, { headers });
      }),
      map((raw) => {
        // Handle wrapped response if needed
        const res: any = raw?.data ?? raw;
        return {
          uuid: res?.id ?? res?.uuid ?? dashboardId,
          projectId: res?.projectId ?? projectId,
          templateId: res?.templateId,
          title: res?.title ?? newName,
          widgets: res?.widgets ?? res?.widgetConfigs ?? [],
          createdAt: res?.createdAt ?? new Date().toISOString(),
          updatedAt: res?.updatedAt ?? new Date().toISOString(),
          plotUuid: res?.plotUuid,
        } as Dashboard;
      }),
      tap({
        next: (res) =>
          dashLog('renameDashboard OK', {
            uuid: (res as any)?.uuid ?? (res as any)?.id,
            title: (res as any)?.title,
          }),
        error: (err) =>
          dashError('renameDashboard FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  deleteDashboard(projectId: string, dashboardId: string): Observable<void> {
    dashGroupCollapsed('API deleteDashboard', { projectId, dashboardId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.delete<void>(`${this.API_URL}/api/${projectId}/dashboards/${dashboardId}`, { headers });
      }),
      tap({
        next: () =>
          dashLog('deleteDashboard OK', {
            dashboardId,
          }),
        error: (err) =>
          dashError('deleteDashboard FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Reports
   */
  generateReport(projectId: string, dashboardId: string): Observable<any> {
    dashGroupCollapsed('API generateReport', {
      projectId,
      dashboardId,
    });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<any>(`${this.baseUrl}/api/${projectId}/dashboards/${dashboardId}/reports`, {}, { headers });
      }),
      map(response => {
        // Handle wrapped response if needed
        const unwrapped = response?.data || response;
        dashLog('generateReport raw response', {
          wrapped: !!(response as any)?.data,
          keys: unwrapped ? Object.keys(unwrapped) : [],
        });
        return unwrapped;
      }),
      tap({
        next: (res) =>
          dashLog('generateReport OK', {
            id: (res as any)?.id,
            status: (res as any)?.status,
            widgetCount: (res as any)?.data?.widgetCount ?? (res as any)?.widgetCount,
            plotUuid: (res as any)?.plotUuid ?? (res as any)?.data?.plotUuid,
          }),
        error: (err) =>
          dashError('generateReport FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  getReports(projectId: string, dashboardId: string): Observable<ReportListItem[]> {
    dashGroupCollapsed('API getReports', { projectId, dashboardId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get<any>(`${this.baseUrl}/api/${projectId}/dashboards/${dashboardId}/reports`, { headers });
      }),
      map(response => {
        dashLog('getReports raw response', {
          isArray: Array.isArray(response),
          wrapped: !!(response as any)?.data,
          dataCount: Array.isArray((response as any)?.data) ? (response as any).data.length : undefined,
        });
        // Handle both direct array and wrapped response
        if (Array.isArray(response)) {
          return response;
        }
        return response?.data || [];
      }),
      tap({
        next: (res) =>
          dashLog('getReports OK', {
            count: Array.isArray(res) ? res.length : 0,
            latestId: Array.isArray(res) && res.length ? res[0]?.id : undefined,
          }),
        error: (err) =>
          dashError('getReports FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  getReport(projectId: string, dashboardId: string, reportId: string): Observable<ReportDetail> {
    dashGroupCollapsed('API getReport', { projectId, dashboardId, reportId });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get<any>(`${this.baseUrl}/api/${projectId}/dashboards/${dashboardId}/reports/${reportId}`, { headers });
      }),
      map(response => {
        // Handle wrapped response if needed
        const unwrapped = response?.data || response;
        dashLog('getReport raw response', {
          wrapped: !!(response as any)?.data,
          keys: unwrapped ? Object.keys(unwrapped) : [],
        });
        return unwrapped;
      }),
      tap({
        next: (res) => {
          const widgets = (res as any)?.data?.widgets;
          dashLog('getReport OK', {
            id: (res as any)?.id,
            status: (res as any)?.status,
            plotUuid: (res as any)?.plotUuid ?? (res as any)?.data?.plotUuid,
            widgetsCount: widgets && typeof widgets === 'object' ? Object.keys(widgets).length : 0,
          });
        },
        error: (err) =>
          dashError('getReport FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Получить отчет по токену, projectId, dashboardId и reportId (для PDF экспорта)
   * Публичный метод - использует токен из URL вместо обычной авторизации
   * Использует тот же endpoint что и обычный getReport для совместимости
   */
  getReportByToken(token: string, projectId: string, dashboardId: string, reportId: string): Observable<ReportDetail> {
    const url = `${this.baseUrl}/api/${projectId}/dashboards/${dashboardId}/reports/${reportId}`;
    console.log('[PDF-EXP] url', url);
    dashGroupCollapsed('API getReportByToken', { projectId, dashboardId, reportId, token: maskToken(token) });
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${token}`
      // Не добавляем x-company header - projectId уже в URL
    });
    return this.http.get<any>(`${this.baseUrl}/api/${projectId}/dashboards/${dashboardId}/reports/${reportId}`, { headers }).pipe(
      map(response => {
        // Handle wrapped response if needed
        const unwrapped = response?.data || response;
        dashLog('getReportByToken raw response', {
          wrapped: !!(response as any)?.data,
          keys: unwrapped ? Object.keys(unwrapped) : [],
        });
        return unwrapped;
      }),
      tap({
        next: (res) => {
          const widgets = (res as any)?.data?.widgets;
          dashLog('getReportByToken OK', {
            id: (res as any)?.id,
            status: (res as any)?.status,
            plotUuid: (res as any)?.plotUuid ?? (res as any)?.data?.plotUuid,
            widgetsCount: widgets && typeof widgets === 'object' ? Object.keys(widgets).length : 0,
          });
        },
        error: (err) =>
          dashError('getReportByToken FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Получить последний сгенерированный отчет
   * Сначала получает список отчетов, находит последний по createdAt, затем получает его детали
   */
  getLatestReport(projectId: string, dashboardId: string): Observable<ReportDetail | null> {
    return this.getReports(projectId, dashboardId).pipe(
      switchMap(reports => {
        if (!reports || reports.length === 0) {
          return of(null);
        }

        // Сортируем отчеты по createdAt (от нового к старому) и берем первый
        const sortedReports = [...reports].sort((a, b) => {
          const dateA = new Date(a.createdAt).getTime();
          const dateB = new Date(b.createdAt).getTime();
          return dateB - dateA; // Сортировка по убыванию (новые первыми)
        });

        const latestReport = sortedReports[0];

        if (!latestReport || !latestReport.id) {
          return of(null);
        }

        // Получаем детали последнего отчета
        return this.getReport(projectId, dashboardId, latestReport.id);
      })
    );
  }

  /**
   * Generic Widget Data Fetcher
   * Fetches data for a specific widget based on its configuration
   */
  getWidgetData(endpoint: string, params: Record<string, string> = {}, projectId?: string): Observable<any> {
    let httpParams = new HttpParams();
    Object.keys(params).forEach(key => {
      httpParams = httpParams.set(key, params[key]);
    });

    // Determine if the endpoint is absolute or relative
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;

    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.get(url, { params: httpParams, headers });
      })
    );
  }

  /**
   * Get Isochrone data for a plot record
   * Returns GeoJSON FeatureCollection with isochrone polygons
   */
  getIsochrone(
    plotRecordUuid: string,
    contoursMeters: number[],
    profile: string = 'driving',
    projectId?: string
  ): Observable<FeatureCollection> {
    dashGroupCollapsed('API getIsochrone', {
      projectId,
      plotRecordUuid,
      contoursMeters,
      profile,
    });

    const requestBody = {
      uuid: plotRecordUuid,
      profile: profile,
      contours_meters: contoursMeters,
      polygons: true,
      denoise: 0.5,
      generalize: 50,
    };

    return this.getPerareApiKey().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<any>(`${this.baseUrl}/perare-api/v1/isochrone`, requestBody, { headers });
      }),
      map(response => {
        // Handle wrapped response: response.data.data contains the GeoJSON FeatureCollection
        const isochroneData = response?.data?.data || response?.data || response;

        dashLog('getIsochrone response (summary)', {
          hasData: !!isochroneData,
          isFeatureCollection: isochroneData?.type === 'FeatureCollection',
          featuresCount: isochroneData?.features?.length || 0,
        });

        if (!isochroneData || isochroneData.type !== 'FeatureCollection') {
          dashWarn('getIsochrone: invalid response format', {
            responseType: isochroneData?.type,
            responseKeys: isochroneData ? Object.keys(isochroneData) : [],
          });
          // Return empty FeatureCollection if invalid
          return {
            type: 'FeatureCollection',
            features: [],
          } as FeatureCollection;
        }

        return isochroneData as FeatureCollection;
      }),
      tap({
        next: (res) =>
          dashLog('getIsochrone OK', {
            featuresCount: res?.features?.length || 0,
          }),
        error: (err) =>
          dashError('getIsochrone FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Assess market position and pricing of a plot using nearby listings.
   * POST /perare-api/v1/market-position
   */
  getMarketPosition(
    params: MarketPositionRequest,
    projectId?: string
  ): Observable<MarketPositionResponse | null> {
    dashGroupCollapsed('API getMarketPosition', {
      projectId,
      uuid: params.uuid,
      hasIsochrone: !!params.isochrone_geojson,
    });

    return this.getPerareApiKey().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<MarketPositionApiResponse>(
          `${this.baseUrl}/perare-api/v1/market-position`,
          params,
          { headers }
        );
      }),
      map(response => {
        const data = response?.data ?? null;
        dashLog('getMarketPosition response', {
          result: response?.result,
          hasData: !!data,
        });
        if (response?.result === 'error' || !data) {
          dashWarn('getMarketPosition: no data or error', {
            msg: response?.msg,
          });
          return null;
        }
        return data;
      }),
      tap({
        next: res =>
          dashLog('getMarketPosition OK', { hasData: !!res }),
        error: err =>
          dashError('getMarketPosition FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Prepare PDF export by sending report data to backend
   * Backend saves data and returns sessionId and exportUrl
   */
  preparePdfExport(reportData: ReportDetail, projectId: string, dashboardId: string): Observable<{ sessionId: string; exportUrl: string }> {
    dashGroupCollapsed('API preparePdfExport', { projectId, dashboardId });
    
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        const requestBody = {
          reportData,
          projectId,
          dashboardId
        };
        
        return this.http.post<{ sessionId: string; exportUrl: string }>(
          `${this.baseUrl}/api/pdf-export/prepare`,
          requestBody,
          { headers }
        );
      }),
      tap({
        next: (res) =>
          dashLog('preparePdfExport OK', {
            sessionId: res?.sessionId,
            hasExportUrl: !!res?.exportUrl,
          }),
        error: (err) =>
          dashError('preparePdfExport FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }

  /**
   * Export dashboard to PDF
   * Backend uses Playwright to generate PDF and returns it as Blob
   */
  exportPdf(projectId: string, dashboardId: string, printMode: boolean = false): Observable<Blob> {
    dashGroupCollapsed('API exportPdf', { projectId, dashboardId, printMode });
    return this.getAuthToken().pipe(
      switchMap(apiKey => {
        const headers = this.createAuthHeaders(apiKey, projectId);
        return this.http.post<Blob>(
          `${this.baseUrl}/api/pdf-export/export`,
          { projectId, dashboardId, printMode },
          {
            headers,
            responseType: 'blob' as 'json',
          }
        );
      }),
      tap({
        next: (blob) =>
          dashLog('exportPdf OK', {
            blobSize: blob?.size || 0,
            blobType: blob?.type || 'unknown',
          }),
        error: (err) =>
          dashError('exportPdf FAILED', {
            status: (err as any)?.status,
            message: (err as any)?.message,
            response: safeStringify((err as any)?.error),
          }),
      }),
      finalize(() => dashGroupEnd())
    );
  }
}

